import { useState, type ReactNode } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQueries,
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  useProjectAdoptionsQuery,
  type LibraryAsset,
} from "@/api/library";
import { isTerminalQueryError } from "@/api/queryClient";
import { useRetryPausedQueryByKey } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import {
  adoptionLock,
  adoptionRefusal,
  type AdoptionDecision,
} from "@/lib/adoptions";
import {
  questionnaireAdoptionDetail,
  questionnaireVersionRefs,
} from "@/lib/questionnaireAdoption";
import { sectionAdoptionCandidates } from "@/lib/sectionAdoption";
import { Notice } from "@/ui/Notice";
import { ProjectSectionPane } from "@/ui/project/ProjectSectionPane";
import { EmptyState, StaleDataNotice } from "@/ui/states";

import { AdoptionDecisionDialog } from "./AdoptionDecisionDialog";
import { AdoptFromLibraryAction } from "./AdoptFromLibraryAction";
import { AdoptedRow } from "./AdoptionRow";

/**
 * Why the roster a clinician can adopt from is the length it is (#638).
 *
 * Stated on the surface that offers the adoption, in a clinician's terms: an
 * instrument is here because it was reviewed and licensed, and one that is
 * missing is missing for that reason rather than because a read failed. The
 * per-row refusals say the rest.
 */
const GOVERNED_INVENTORY_NOTICE =
  "Questionnaires are a governed family: only instruments approved and licensed through clinical review can be newly adopted, and none can be authored in the Editor. A version this project already pins keeps running at its exact version even if the catalog changes.";

/**
 * The Questionnaires destination in the Clinical configuration rail (#489).
 *
 * ## WHY THIS EXISTS RATHER THAN A SEVENTH SECTION
 *
 * `questionnaires` is NOT a member of the contract's `SectionSlug`
 * (`contracts/generated/editor-service.d.ts`), and `SectionSurface`'s guard says
 * outright that sending it to `getProjectSection` "is the mistake this guard
 * exists to make impossible". The ticket asks for an intentional Questionnaire
 * destination and forbids inventing an unsupported slug, so this surface is
 * reached by the same slug-specific mechanism `summary` already uses: the rail
 * entry declares `projection: "none"`, `SectionSurface` routes it here, and
 * `isSectionSlug()` keeps gating the projection fetch for everything else. No
 * `SectionSlug` member was added and no endpoint was invented.
 *
 * ## WHAT IT RENDERS — #369'S WORK, NOT A NEW READING OF IT
 *
 * The project's pinned questionnaires, each stating what the pinned instrument
 * actually administers. Every fact comes from modules #369 already shipped:
 * `questionnaireVersionRefs` chooses the reads, `questionnaireAdoptionDetail`
 * classifies each one, and `PinnedQuestionnaire` renders it. This module is the
 * wiring the ticket asks for — coordination with #369, not an expansion of it.
 *
 * ## THE PINNED VERSION, NEVER THE HEAD
 *
 * `questionnaireVersionRefs` keys each read by the version the PIN names, so a
 * publish in the shared library cannot move a number on this screen. That rule
 * is #369's and is inherited whole by reusing its functions rather than
 * re-deriving them here.
 *
 * ## NO SECOND PIN SET, AND NO SECOND WRITE PATH
 *
 * The pin set and the catalog are read through the same hooks — and therefore
 * the same cache entries — as every other adoption surface, so a clinician
 * moving between sections observes one pin set rather than several.
 *
 * This destination carries the WHOLE adoption action for the questionnaire
 * family — adopt, move the pin, remove it — and it has to, because no other
 * surface can. `/adoptions` held all three and is a moved notice now; the
 * clinical sections took over the other eight families, but `questionnaires` is
 * deliberately not a `SectionSlug`, so no section projection ever reports a
 * questionnaire and no section block can offer a control over one; #373's
 * whole-pin-set view is read-only by design.
 *
 * Both halves of that were learned the hard way, one review round each. The
 * first round of #489 moved the evidence and left ADOPT-NEW behind on the
 * retired tab. The second round restored adopt-new here and left UPDATE and
 * UNPIN behind, on the reasoning — written in this very comment — that changing
 * a pin "lives on the clinical section that reports the item". For this family
 * there is no such section, so that sentence described a surface that does not
 * exist and the capability had simply been deleted for questionnaires: a
 * project could pin PHQ-9 v1, watch v2 publish, and have no way to take either
 * action anywhere in the product (Codex, #511 round 2).
 *
 * IT STILL WRITES NOTHING ITSELF. `AdoptedRow` and `AvailableRow` are
 * `/adoptions`' own rows, reused unchanged, and both only call `onDecide`;
 * `AdoptionDecisionDialog` is #372's own confirmation, also unchanged, which
 * computes the whole-set `PUT /adoptions` replacement and states its impact
 * before submitting under the `adoptionsVersion` it loaded. There is still
 * exactly one route into changing an adoption.
 *
 * THE INVENTORY IS GOVERNED (#638), and this surface states it once rather
 * than leaving it to be inferred from a short list. Ratified §2.2 admits only
 * approved/licensed instruments for new adoption and keeps arbitrary authoring
 * disabled, so what a clinician can pin here is a roster somebody reviewed —
 * and a picker that simply happened to be short would be indistinguishable from
 * a broken one. The rows themselves carry the per-row verdict: `adoptionRefusal`
 * reads `capabilities.licensedInventory`, which the service computes from the
 * seed catalog's own licensing declarations. Nothing here holds a roster of
 * approved names, and nothing here decides whether an instrument is licensed.
 *
 * THE LOCK IS THE CATALOG'S OWN DECLARATION, and that is not a shortcut here.
 * `ProjectAdoptions` refined its locks with `adoptionProvenanceByAsset` over
 * the section projection, which inverts `FieldState.origin` — and a
 * questionnaire pin never appears there, because no section serves questionnaire
 * fields. There is therefore no engine-reported lock for this family to miss,
 * and `adoptionLock` falling back to `AssetCapabilities.lockedFloor` is the
 * whole of the available truth rather than a weaker reading of it. Seven
 * projection reads would be issued to learn nothing.
 */
export function ProjectQuestionnaires({
  projectId,
  sectionLabel = "Questionnaires",
  controls,
}: {
  projectId: string;
  /**
   * The section's own name from the rail, for the pane's accessible heading
   * (#909). Defaulted: this component serves exactly one rail slug.
   */
  sectionLabel?: string;
  /**
   * Section-scoped controls above the rows, or absent (#909).
   *
   * `ProjectSectionPane`'s stable insertion point, so a later leaf adds a
   * control here the same way it does on every other Project section.
   */
  controls?: ReactNode;
}) {
  const adoptions = useProjectAdoptionsQuery(projectId);
  const catalog = useLibraryAssetsQuery(true);
  const write = useCapability("draft.write");
  // The SERVED cardinality policy (#634). A pin on a family the server bounds
  // at one is a replacement, and `AdoptionDecisionDialog` cannot describe which
  // pin it displaces without this document — so an unread policy becomes a
  // STATED refusal on the adopt control rather than a control whose only
  // outcome is that refusal (ADR 0001). Removal is deliberately not gated on
  // it: an un-pin is the recovery path out of every degraded state (#612).
  const policyUnread = !useLibraryPlacementRegistryQuery().isSuccess;
  const [decision, setDecision] = useState<AdoptionDecision | null>(null);

  // Derived above the branches below so the hook's argument never depends on
  // which branch renders — the arrangement `ProjectAdoptions` and
  // `SectionAdoptionPanel` both use. A conditional call is a conditional hook,
  // and an empty list is a legal argument that fetches nothing.
  const terminalCatalogFailure =
    catalog.isError && isTerminalQueryError(catalog.error);
  // A transient failed refresh may keep the last authorized response visible,
  // with an explicit stale-data notice. A terminal answer (especially 401/403)
  // must fail closed even when TanStack Query still carries an older response.
  const readableCatalog = terminalCatalogFailure ? undefined : catalog.data;
  const assetsById = new Map<string, LibraryAsset>(
    (readableCatalog?.items ?? []).map((asset) => [asset.id, asset]),
  );
  const pins = adoptions.data?.items ?? [];
  const versions = useAssetVersionQueries(questionnaireVersionRefs(pins));
  // Per ROW rather than per surface, exactly as `ProjectAdoptions` does it: a
  // retry that named one questionnaire and re-read every failed one is the
  // global-versus-per-asset confusion #443's review round removed.
  const retryVersion = useRetryPausedQueryByKey();

  // The pin set is the subject. Until it answers there is nothing trustworthy
  // to list. The catalog is enrichment: its pending state becomes an inline
  // notice after the pin read, so it cannot erase exact stored pin links.
  //
  // THE SHELL SURVIVES BOTH (Codex, #909 round 2). These returned bare query
  // states, so the section's accessible heading — and anything a later leaf
  // supplied through `controls` — vanished while the pin set was loading or
  // after it failed. The pane is the section; what a degraded read removes is
  // the ADOPTION ACTION, because the pin set is not current and no write could
  // be described, which is the same gate `SectionAdoptionPanel` applies.
  if (adoptions.isPending) {
    return (
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        {...(controls ? { controls } : {})}
      >
        <QueryPendingState
          isPaused={adoptions.isPaused}
          failureReason={adoptions.failureReason}
          label="Loading this project's questionnaires"
          errorTitle="This project's questionnaires could not be loaded"
          rows={3}
          queryKey={queryKeys.projectAdoptions(projectId)}
        />
      </ProjectSectionPane>
    );
  }

  if (adoptions.isError) {
    return (
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        {...(controls ? { controls } : {})}
      >
        <QueryErrorState
          error={adoptions.error}
          subject="Questionnaires"
          title="This project's questionnaires could not be loaded"
          description="The Editor Service did not return this project's pins, so which instruments it administers is not known here."
          onRetry={() => void adoptions.refetch()}
        />
      </ProjectSectionPane>
    );
  }

  // THE PIN SET ANSWERED; THE CATALOG MAY NOT HAVE (#615). A failed catalog
  // read used to replace this whole surface with one error, which erased the
  // exact stored pins along with it — and the ticket's failure-state
  // criterion is the opposite: while the exact pin is known, the way back to
  // its authoritative library asset stays. Each pin carries its own family
  // (`assetType`, joined server-side), so the rows below can be listed and
  // addressed from the pin set alone. What the catalog uniquely supplies —
  // the name, the head, the archived flag, what can be adopted next — is
  // stated as missing, once, above them; nothing is guessed in its place.
  const catalogNotice = catalog.isPending ? (
    <Notice>
      The shared library catalog is still loading. Each stored questionnaire pin
      remains listed below by id, exact version, family, and exact library page;
      names, library-head comparisons, and adoption controls appear after the
      catalog answers.
    </Notice>
  ) : catalog.isError && catalog.data && !terminalCatalogFailure ? (
    <StaleDataNotice
      updatedAt={catalog.dataUpdatedAt}
      onRetry={() => void catalog.refetch()}
    />
  ) : catalog.isError ? (
    <QueryErrorState
      error={catalog.error}
      subject="Library assets"
      title="The pinned questionnaires could not be named"
      description="The shared library catalog that names this project's pins did not load. Each pin below is listed by its stored identity — the exact version and its library page — without its name, its library head, or what it administers, and nothing further can be adopted until the catalog answers."
      onRetry={() => void catalog.refetch()}
    />
  ) : null;

  // The confirmation dialog deliberately requires a current successful
  // catalog read. Keep each row and exact read-only link visible while that
  // read is unsettled, but do not offer a control the dialog cannot execute.
  const catalogDecisionRefusal = catalog.isSuccess
    ? null
    : "The shared library catalog must answer before this adoption can change.";

  // Which pins are questionnaires is the PIN SET's own fact (#615): the
  // service joins `assetType` onto each stored pin, so a pin the catalog did
  // not name is still listed here as what it is, rather than dropped for a
  // type that was never actually unknown. Nothing is guessed — the filter is
  // the stored family, exactly as `questionnaireVersionRefs` reads it.
  const questionnairePins = pins.filter(
    (pin) => pin.assetType === "questionnaire",
  );

  // Candidates need the catalog, and only the catalog can say there are none:
  // `null` here is "not known", never "nothing to adopt". When the catalog
  // answered, `sectionAdoptionCandidates` cannot answer `null` — both reads
  // are in hand — so `?? []` is that narrowing and not a fallback.
  const candidates = catalog.isSuccess
    ? (
        sectionAdoptionCandidates({
          section: "questionnaires",
          catalog: catalog.data.items,
          pins,
        }) ?? []
      ).map((asset) => ({
        asset,
        // `held: false` by construction: a candidate is an asset this project
        // does not pin, so this is an ADD and never a move.
        refusal: adoptionRefusal("pin", {
          asset,
          lock: adoptionLock(asset, undefined),
          held: false,
          missingWriteReason: write.reason,
          policyUnread,
        }),
      }))
    : null;

  // LOVABLE'S TOP-RIGHT ACTION (#909 round 2). What stood here was this
  // destination's own bottom-of-page block: an `Adopt a questionnaire from the
  // shared clinical library` heading, a whole-set-replacement paragraph, then
  // the rows — the exact shape #909 removed from the other seven sections
  // while `SectionSurface` routed this slug past the change. Same derivation,
  // same `AvailableRow`s, same `AdoptionDecisionDialog`; only the placement.
  const adoptAction = (
    <AdoptFromLibraryAction
      sectionLabel={sectionLabel}
      candidates={candidates}
      onDecide={setDecision}
    />
  );

  const dialog = decision ? (
    <AdoptionDecisionDialog
      projectId={projectId}
      decision={decision}
      onClose={() => setDecision(null)}
    />
  ) : null;

  // The notices are `controls`, not `details`: an unread catalog and the
  // governed-inventory rule are both things an author must read BEFORE the
  // list, which is what that slot is for.
  const notices = (
    <>
      {controls}
      {catalogNotice}
      <Notice>{GOVERNED_INVENTORY_NOTICE}</Notice>
    </>
  );

  if (questionnairePins.length === 0) {
    return (
      <>
        <ProjectSectionPane
          sectionLabel={sectionLabel}
          primaryAction={adoptAction}
          controls={notices}
        >
          <EmptyState
            title="This project pins no questionnaire"
            description="Nothing from the shared clinical library's questionnaires is adopted into this project, so no instrument is administered from here."
          />
        </ProjectSectionPane>
        {dialog}
      </>
    );
  }

  return (
    <>
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        primaryAction={adoptAction}
        controls={notices}
      >
        <p className="text-sm text-muted-foreground">
          Every questionnaire below is described at the{" "}
          <strong>exact version this project pins</strong>, not at the shared
          library&apos;s current one — a pin names an immutable version, so a
          later publish never moves it.
        </p>

        {/*
        `AdoptedRow` rather than a row of this module's own, and that is the
        second round's fix rather than a tidy-up. It is `/adoptions`' pinned
        row: it already renders #369's questionnaire detail through
        `PinnedQuestionnaire` with the same per-row retry, states the pinned
        version through `ReadOnlyField` so a locked adoption explains itself in
        ADR 0001's words, and carries the two controls this destination had
        nowhere else to get — `Update <name> to <head>` where the library has
        moved past the pin, and `Unpin <name>` always. A clinician meeting the
        same decision twice meets the same sentence, because it is the same
        component.

        `suppliedSections` is empty, and truthfully so: that list comes from
        `adoptionProvenanceByAsset` over `FieldState.origin`, and no section
        serves questionnaire fields, so there is no attribution to render. The
        row omits the line entirely rather than claiming anything.
      */}
        <ul className="space-y-3">
          {questionnairePins.map((pin) => {
            const asset = assetsById.get(pin.assetId);
            const detail = questionnaireAdoptionDetail(pin, asset, versions);
            const lock = adoptionLock(asset, undefined);

            return (
              <AdoptedRow
                key={pin.assetId}
                pin={pin}
                asset={asset}
                lock={lock}
                suppliedSections={[]}
                questionnaire={detail}
                {...(detail?.state === "unread" && detail.recovery === "reread"
                  ? {
                      onRetryQuestionnaire: () => {
                        retryVersion(
                          queryKeys.assetVersion(pin.assetId, pin.version),
                        );
                      },
                    }
                  : {})}
                // `held: true` on both: every row here is a pin this project
                // holds, so the `pin` decision is a MOVE and `adoptionRefusal`
                // refuses it under a lock exactly as it refuses the removal.
                upgradeRefusal={
                  adoptionRefusal("pin", {
                    asset,
                    lock,
                    held: true,
                    missingWriteReason: write.reason,
                    policyUnread,
                  }) ?? catalogDecisionRefusal
                }
                unpinRefusal={
                  adoptionRefusal("unpin", {
                    asset,
                    lock,
                    held: true,
                    missingWriteReason: write.reason,
                    // NEVER the policy on a removal: the un-pin is the recovery
                    // path out of every degraded state, and no read may take it
                    // away (#612).
                    policyUnread: false,
                  }) ?? catalogDecisionRefusal
                }
                onDecide={setDecision}
              />
            );
          })}
        </ul>
      </ProjectSectionPane>
      {dialog}
    </>
  );
}
